import { StandardContainer } from '@design-system/StandardContainer';
import { Button } from '@design-system/Button';
import { useNavigate } from 'react-router-dom';
import * as P from '@/constants/paths';
import { useUserStore } from '@hooks/useUserStore';
import { useQueryClient } from '@tanstack/react-query';
import { useLogoutQuery } from '@viome/viome-base/src/usecase/Login/LoginQuery';
import { LocalStorage } from '@utils/LocalStorage';
import { LoadingPage } from '@screens/LoadingPage';
import { Sentry } from '@/main';
import { toast } from '@hooks/use-toast';

type NoActiveKitsPageProps = {
  type: 'nokitregister' | 'cancerdetect';
};

export const NoActiveKitsPage = ({ type }: NoActiveKitsPageProps) => {
  const navigate = useNavigate();
  const { setUser } = useUserStore();
  const queryClient = useQueryClient();
  const { refetch: logout, isLoading } = useLogoutQuery({ enabled: false });

  const clearAllCachesAndState = () => {
    queryClient.clear();
    setUser(undefined);
    Sentry.setUser(null);
    LocalStorage.clear();
    LocalStorage.removeItem('user');
    LocalStorage.removeItem('bootup');
    LocalStorage.removeItem('auth_token');
    sessionStorage.clear();
  };

  const handleLogout = async () => {
    try {
      const response = await logout();

      if (response?.isError) {
        toast({
          variant: 'destructive',
          description: 'Something went wrong, please try again',
        });
      }
      clearAllCachesAndState();
    } catch (error) {
      toast({
        variant: 'destructive',
        description: `Something went wrong, please try again ${error}`,
      });
    }
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div className="min-h-screen bg-white">
      <StandardContainer>
        <div className="h-screen flex flex-col items-center justify-center">
          <img
            className="w-16 h-16 mb-4"
            src="https://res.cloudinary.com/dttltpafi/image/upload/v1738144056/Frame_2147214825_p0crgv.png"
            alt="Error"
            loading="lazy"
          />

          {type === 'nokitregister' ? (
            <>
              <div className="text-center mb-12">
                <p className="typography-body text-primary">
                  You currently have no active kits.
                  <br />
                  Before you can login, you must register a kit.
                </p>
              </div>

              <div className="w-full max-w-md space-y-4 px-4 md:px-0">
                <Button
                  variant="default"
                  className="w-full py-6 rounded-full bg-black text-white hover:bg-black/90"
                  onClick={() => navigate(P.REGISTRATION_KIT_ID)}
                >
                  Register a Kit
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-primary hover:bg-transparent hover:text-primary/80"
                  onClick={handleLogout}
                >
                  Login as a different user
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-12">
                <p className="typography-body text-primary">
                  For Oral Health Pro™ and CancerDetect™ tests,
                  <br />
                  go to <span className="underline">my.viome.com</span> in a
                  browser.
                </p>
              </div>

              <div className="w-full max-w-md space-y-6 px-4 md:px-0">
                <Button
                  variant="default"
                  className="w-full py-6 rounded-full bg-black text-white"
                  onClick={() => navigate(P.REGISTRATION_KIT_ID)} //todo:replace with register path
                >
                  Register a Kit
                </Button>
                <Button
                  variant="ghost"
                  className="w-full py-6 rounded-full bg-white text-primary"
                  onClick={() =>
                    (window.location.href = 'https://my.viome.com')
                  }
                >
                  Go to my.viome.com
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-primary"
                  onClick={handleLogout}
                >
                  Login as a different user
                </Button>
              </div>
            </>
          )}
        </div>
      </StandardContainer>
    </div>
  );
};
